import { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../../components/Button';
import Select from '../../components/Select';
import Input from '../../components/Input';
import Table, { Column } from '../../components/Table';

const REPORTS = [
  { key: 'appraisal-summary', label: 'Appraisal Summary' },
  { key: 'recommendation', label: 'Recommendation Report' },
  { key: 'bell-curve', label: 'Bell Curve Report' },
  { key: 'appraisal-detail', label: 'Appraisal Detail' },
] as const;

const MONTHS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1).padStart(2, '0') }));

interface Template {
  id: string;
  name: string;
}

export default function AppraisalReports() {
  const [active, setActive] = useState<(typeof REPORTS)[number]>(REPORTS[0]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [assignmentId, setAssignmentId] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [detail, setDetail] = useState<any | null>(null);
  const [loading, setLoading] = useState(false);
  const isDetail = active.key === 'appraisal-detail';

  useEffect(() => {
    api.get('/pas/masters/templates').then((res) => {
      const list = res.data.templates || [];
      setTemplates(list);
      if (list.length && !templateId) setTemplateId(list[0].id);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    setLoading(true);
    try {
      if (isDetail) {
        if (!assignmentId) return;
        const res = await api.get('/pas/reports/appraisal-detail', { params: { assignmentId } });
        setDetail(res.data);
      } else {
        if (!templateId) return;
        const res = await api.get(`/pas/reports/${active.key}`, { params: { templateId, month, year } });
        setRows(res.data.rows || []);
      }
    } finally {
      setLoading(false);
    }
  };

  const exportExcel = async () => {
    const params = isDetail ? { assignmentId, format: 'excel' } : { templateId, month, year, format: 'excel' };
    const res = await api.get(`/pas/reports/${active.key}`, { params, responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `${active.key}.xlsx`;
    a.click();
  };

  const columns: Column<any>[] = rows.length > 0 ? Object.keys(rows[0]).map((k) => ({ key: k, header: k.replace(/_/g, ' ').toUpperCase() })) : [];

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Appraisal Reports</h1>
      <div className="flex gap-2 mb-4 border-b overflow-x-auto">
        {REPORTS.map((r) => (
          <button
            key={r.key}
            onClick={() => { setActive(r); setRows([]); setDetail(null); }}
            className={`px-4 py-2 text-sm whitespace-nowrap ${active.key === r.key ? 'border-b-2 border-blue-600 text-blue-600 font-medium' : 'text-gray-500'}`}
          >
            {r.label}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 grid grid-cols-4 gap-4 items-end">
        {isDetail ? (
          <div className="col-span-3">
            <Input label="Assignment ID" value={assignmentId} onChange={(e) => setAssignmentId(e.target.value)} placeholder="Paste an assignment ID from Assign Appraisal" />
          </div>
        ) : (
          <>
            <Select label="Template" value={templateId} onChange={(e) => setTemplateId(e.target.value)} options={templates.map((t) => ({ value: t.id, label: t.name }))} />
            <Select label="Month" value={month} onChange={(e) => setMonth(e.target.value)} options={MONTHS} />
            <Input label="Year" type="number" value={year} onChange={(e) => setYear(e.target.value)} />
          </>
        )}
        <div className="flex gap-2">
          <Button onClick={run} loading={loading}>Run</Button>
          <Button variant="secondary" onClick={exportExcel} disabled={isDetail ? !detail : !rows.length}>Export Excel</Button>
        </div>
      </div>

      {isDetail ? (
        detail ? (
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="text-sm text-gray-600">
              {detail.assignment.employee_code} — {detail.assignment.employee_name} · {detail.assignment.template_name} · {detail.assignment.period_month}/{detail.assignment.period_year}
            </div>
            {detail.levels.map((lr: any) => (
              <div key={lr.id} className="border rounded p-4">
                <div className="font-medium mb-2">
                  Level {lr.level === 0 ? 'Self' : lr.level} — {lr.rater_name} — Score: {lr.quantitative_score}% — Grade: {lr.grade_name || '—'}
                </div>
                {lr.evaluation_comments && <div className="text-sm text-gray-600 mb-2">Comments: {lr.evaluation_comments}</div>}
                <div className="text-xs text-gray-500">{lr.factorScores.length} factor(s) rated, {lr.qualitativeResponses.length} qualitative response(s)</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-sm text-gray-500 p-6 bg-white rounded-lg shadow">Enter an assignment ID and run to see the full form.</div>
        )
      ) : (
        <Table columns={columns} rows={rows} loading={loading} emptyMessage="Run the report to see results" />
      )}
    </div>
  );
}
